import { atom, selectorFamily } from 'recoil';

/** `idDuAn:activeTab` → danh sách key cột đang ẩn */
export type TableHiddenColumnsMap = Record<string, string[]>;

const STORAGE_KEY_MAP = 'cvda-table-hidden-cols-map';
const LEGACY_PREFIX = 'cvda-table-hidden-cols:';

/** Xóa prefs ẩn/hiện cột khi logout — gọi từ clearAuthAll / trang logout */
export function clearTableHiddenColumnsStorage(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY_MAP);
    const legacyKeys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(LEGACY_PREFIX)) legacyKeys.push(k);
    }
    legacyKeys.forEach((k) => localStorage.removeItem(k));
  } catch (error) {
    console.error('Error clearing table hidden columns storage:', error);
  }
}

function loadFromStorage(): TableHiddenColumnsMap {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY_MAP);
    if (raw) {
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as TableHiddenColumnsMap;
      }
    }
    const legacy: TableHiddenColumnsMap = {};
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(LEGACY_PREFIX) || k === STORAGE_KEY_MAP) continue;
      const composite = k.slice(LEGACY_PREFIX.length);
      try {
        const v = localStorage.getItem(k);
        if (v) {
          const arr = JSON.parse(v) as unknown;
          if (Array.isArray(arr)) legacy[composite] = arr as string[];
        }
      } catch {
        /* skip */
      }
    }
    if (Object.keys(legacy).length > 0) {
      localStorage.setItem(STORAGE_KEY_MAP, JSON.stringify(legacy));
    }
    return legacy;
  } catch {
    return {};
  }
}

/** Ẩn/hiện cột bảng công việc dự án — chia sẻ giữa tab Bảng & Kết hợp; persist localStorage */
export const tableHiddenColumnsAtom = atom<TableHiddenColumnsMap>({
  key: 'tableHiddenColumns',
  default: {},
  effects: [
    ({ setSelf, onSet }) => {
      if (typeof window !== 'undefined') {
        const loaded = loadFromStorage();
        if (Object.keys(loaded ?? {}).length > 0) {
          setSelf(loaded);
        }
      }
      let persistTimer: ReturnType<typeof setTimeout> | null = null;
      onSet((newValue) => {
        if (typeof window === 'undefined') return;
        if (persistTimer) clearTimeout(persistTimer);
        persistTimer = setTimeout(() => {
          persistTimer = null;
          try {
            const safe =
              newValue && typeof newValue === 'object' && !Array.isArray(newValue)
                ? (newValue as TableHiddenColumnsMap)
                : {};
            localStorage.setItem(STORAGE_KEY_MAP, JSON.stringify(safe));
          } catch (error) {
            console.error('Error saving table hidden columns map:', error);
          }
        }, 250);
      });
    },
  ],
});

const EMPTY_HIDDEN_KEYS: string[] = [];

/** Ẩn/hiện cột: ưu tiên prefs đã lưu; chưa có thì fallback base (không #sheet) rồi defaultHiddenKeys. */
export function resolveHiddenColumnKeysForComposite(
  map: TableHiddenColumnsMap,
  compositeKey: string,
  defaultHiddenKeys: readonly string[] = []
): string[] {
  if (map[compositeKey] !== undefined) {
    return map[compositeKey];
  }
  // Migrate: `{base}#all` ← prefs cũ `{base}` (trước khi tách theo sheet).
  const hashIdx = compositeKey.lastIndexOf('#');
  if (hashIdx > 0) {
    const sheetKey = compositeKey.slice(hashIdx + 1);
    const base = compositeKey.slice(0, hashIdx);
    if (sheetKey === 'all' && map[base] !== undefined) {
      return map[base];
    }
  }
  return defaultHiddenKeys.length ? [...defaultHiddenKeys] : EMPTY_HIDDEN_KEYS;
}

/** Chỉ component đang xem đúng `idDuAn:activeTab` subscribe slice này → giảm re-render khi đổi dự án khác */
export const hiddenColumnKeysForCompositeSelector = selectorFamily({
  key: 'hiddenColumnKeysForComposite',
  get:
    (compositeKey: string) =>
    ({ get }) => {
      const map = get(tableHiddenColumnsAtom);
      return map[compositeKey] ?? EMPTY_HIDDEN_KEYS;
    },
});
